// ═══════════════════════════════════════════════════════════════
// RecommendationsEditor — the work the tech suggests and the
// customer decides on, line by line.
//
// The point of "pull from inspection" is that the tech already
// described every one of these findings while walking the car. Making
// them retype it into a line item is the duplicate typing this whole
// feature exists to remove — so a finding becomes a priced
// recommendation, photos and all, in one tap. All that's left is the
// number.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import {
  Plus, Trash2, Lightbulb, ClipboardCheck, Camera, AlertTriangle,
} from 'lucide-react';
import {
  blankRecommendation, recommendationFromFinding, recommendationsTotal, toPrice,
} from './lib/recommendations.js';
import { formatPrice } from './lib/job-helpers.js';

export default function RecommendationsEditor({
  recommendations = [],
  findings = [],
  onChange,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  // Only offer findings that haven't already been turned into a
  // recommendation, so tapping twice can't duplicate a line.
  const usedKeys = useMemo(
    () => new Set(recommendations.map((r) => r.sourceKey).filter(Boolean)),
    [recommendations]
  );
  const available = useMemo(
    () => findings.filter((f) => !usedKeys.has(f.key)),
    [findings, usedKeys]
  );

  const total = recommendationsTotal(recommendations);

  function patch(id, p) {
    onChange(recommendations.map((r) => (r.id === id ? { ...r, ...p } : r)));
  }
  function remove(id) {
    onChange(recommendations.filter((r) => r.id !== id));
  }
  function addBlank() {
    onChange([...recommendations, blankRecommendation()]);
  }
  function addFromFinding(f) {
    onChange([...recommendations, recommendationFromFinding(f)]);
    if (available.length <= 1) setPickerOpen(false);
  }
  function addAllFindings() {
    onChange([...recommendations, ...available.map(recommendationFromFinding)]);
    setPickerOpen(false);
  }

  return (
    <div className="rec-editor">
      <div className="rec-editor-head">
        <h3 className="rec-editor-title">
          <Lightbulb size={15} /> Recommended work
        </h3>
        {total > 0 && <span className="rec-editor-total mono">{formatPrice(total)}</span>}
      </div>
      <p className="muted small rec-editor-help">
        The customer accepts or declines each of these on the estimate. Anything they pass
        on is kept on file and offered again next visit.
      </p>

      {recommendations.length > 0 && (
        <ul className="rec-list">
          {recommendations.map((r) => (
            <li key={r.id} className={`rec-row rec-${r.urgency}`}>
              <div className="rec-row-main">
                <input
                  className="rec-label"
                  value={r.label}
                  maxLength={120}
                  placeholder="What needs doing"
                  aria-label="Recommendation"
                  onChange={(e) => patch(r.id, { label: e.target.value })}
                />
                <div className="rec-price-wrap">
                  <span>$</span>
                  <input
                    className="rec-price"
                    type="number" inputMode="decimal" min="0" step="0.01"
                    value={r.price === 0 ? '' : r.price}
                    placeholder="0.00"
                    aria-label="Price"
                    onChange={(e) => patch(r.id, { price: toPrice(e.target.value) })}
                  />
                </div>
                <button
                  type="button"
                  className="btn-link danger"
                  onClick={() => remove(r.id)}
                  aria-label="Remove recommendation"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <textarea
                className="rec-detail"
                rows={2}
                maxLength={500}
                placeholder="Why it matters — the customer reads this."
                defaultValue={r.detail}
                onBlur={(e) => {
                  if (e.target.value !== r.detail) patch(r.id, { detail: e.target.value });
                }}
              />

              <div className="rec-row-foot">
                <div className="rec-urgency">
                  <button
                    type="button"
                    className={`rec-urgency-btn insp-attention${r.urgency === 'attention' ? ' active' : ''}`}
                    aria-pressed={r.urgency === 'attention'}
                    onClick={() => patch(r.id, { urgency: 'attention' })}
                  >
                    Soon
                  </button>
                  <button
                    type="button"
                    className={`rec-urgency-btn insp-replace${r.urgency === 'replace' ? ' active' : ''}`}
                    aria-pressed={r.urgency === 'replace'}
                    onClick={() => patch(r.id, { urgency: 'replace' })}
                  >
                    Urgent
                  </button>
                </div>
                {r.photoUrls.length > 0 && (
                  <span className="muted small rec-photo-count">
                    <Camera size={12} /> {r.photoUrls.length} photo
                    {r.photoUrls.length === 1 ? '' : 's'} from the inspection
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {pickerOpen && (
        <div className="rec-picker">
          <div className="rec-picker-head">
            <span className="rec-picker-title">
              <ClipboardCheck size={14} /> From the inspection
            </span>
            {available.length > 1 && (
              <button type="button" className="btn-link" onClick={addAllFindings}>
                Add all {available.length}
              </button>
            )}
          </div>
          <ul className="rec-picker-list">
            {available.map((f) => (
              <li key={f.key}>
                <button
                  type="button"
                  className={`rec-picker-btn insp-${f.status}`}
                  onClick={() => addFromFinding(f)}
                >
                  <AlertTriangle size={13} />
                  <span>
                    <strong>{f.label}</strong>
                    <span className="muted small">{f.sectionLabel}</span>
                  </span>
                  {f.photos?.length > 0 && (
                    <span className="muted small"><Camera size={11} /> {f.photos.length}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="btn-link" onClick={() => setPickerOpen(false)}>
            Close
          </button>
        </div>
      )}

      {!pickerOpen && (
        <div className="rec-actions">
          {available.length > 0 && (
            <button type="button" className="btn-secondary" onClick={() => setPickerOpen(true)}>
              <ClipboardCheck size={15} /> Pull from inspection ({available.length})
            </button>
          )}
          <button type="button" className="btn-link" onClick={addBlank}>
            <Plus size={14} /> Add one manually
          </button>
        </div>
      )}

      {recommendations.length === 0 && available.length === 0 && findings.length === 0 && (
        <p className="muted small">
          Run an inspection first and anything you flag shows up here ready to price.
        </p>
      )}
    </div>
  );
}
